import { max, min } from "date-fns";
import { useMemo } from "react";
import {
  AppointmentFieldsFragment,
  AppointmentsQuery,
} from "@/graphql/queries/appointment/appointments.graphql.types";
import useProviderByUser from "@/hooks/appointments/useProviderByUser";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { InvoiceStatus, ReviewStatus, AppointmentStatus } from "@/types";
import { getUserCalendarColor } from "@/utils";
import {
  getEventColorsByState,
  getGeneralGfeAppointmentStatus,
  getUniqServicesProviders,
} from "@/utils/appointment";
import {
  clientHasWalletItems,
  getClientMembershipsList,
} from "@/utils/appointment";
import { hasClientAppointmentFormsToFill } from "@/utils/forms";
import useFeatureFlags from "../common/useFeatureFlags";

export default function useAppointmentEvents(
  appointments: AppointmentsQuery["appointment"]
) {
  const { utcToZonedTimeIso } = useMedspaTimezone();

  const { getProviderIndex, getProviderUserMedspa } = useProviderByUser();
  const { providerColorsConfigEnabled, multiProviderAppointmentsV1Enabled } =
    useFeatureFlags();

  const { mapAppointmentServicesToCalendar } = useCalendarAppointmentServices();

  return useMemo(() => {
    if (!appointments) return [];

    return appointments.map((appointment) => {
      const providerIndex = getProviderIndex(appointment.provider.id);
      const providerUserMedspa = getProviderUserMedspa(appointment.provider.id);

      const { colorObjectByIndex, colorObjectConfigured } =
        getUserCalendarColor({
          ...appointment.provider,
          color: providerUserMedspa?.color,
          userIndex: providerIndex,
        });

      // TODO: it should be refactored/removed after multiProviderAppointmentsV1Enabled is removed
      const eventColorPalette = providerColorsConfigEnabled
        ? colorObjectConfigured
        : colorObjectByIndex;

      const eventColorByStateObject = getEventColorsByState({
        status: appointment.status as AppointmentStatus,
        eventColorPalette,
        providerColorsConfigEnabled,
        multiProviderAppointmentsV1Enabled,
      });

      const earliestServiceDate = multiProviderAppointmentsV1Enabled
        ? min(
            appointment.appointmentServices.flatMap(({ startTime }) =>
              startTime ? new Date(startTime) : []
            )
          )
        : appointment.startTime;

      const latestServiceDate = multiProviderAppointmentsV1Enabled
        ? max(
            appointment.appointmentServices.flatMap(({ endTime }) =>
              endTime ? new Date(endTime) : []
            )
          )
        : appointment.endTime;

      return {
        id: appointment.id,
        start: utcToZonedTimeIso(earliestServiceDate),
        end: utcToZonedTimeIso(latestServiceDate),
        title: `${appointment.client.firstName} ${appointment.client.lastName}`,
        ...eventColorByStateObject,
        extendedProps: {
          isTimeBlock: false,
          isSpecialShift: false,
          status: appointment.status as AppointmentStatus,
          reviewStatus: appointment.reviewStatus as ReviewStatus,
          invoiceStatus: appointment.invoice?.status as InvoiceStatus,
          appointmentServices: mapAppointmentServicesToCalendar(
            appointment.appointmentServices
          ),
          servicesProviders: getUniqServicesProviders(
            appointment.appointmentServices
          ),
          services: appointment.appointmentServices
            .map(
              (appointmentService) => appointmentService.serviceMenuItem.name
            )
            .join(" • "),
          isNewClient:
            appointment.client.appointments.length > 0 &&
            appointment.client.appointments[0].id === appointment.id,
          gfeGeneralStatus: getGeneralGfeAppointmentStatus(
            appointment.appointmentServices
          ),
          hasFormsToFill: hasClientAppointmentFormsToFill(
            appointment.client,
            appointment.id
          ),
          hasWalletItems: clientHasWalletItems(appointment.client),
          clientMembershipsList: getClientMembershipsList(appointment.client),
          eventColorPalette,
          monthViewColors: eventColorByStateObject.monthViewColors,
          iconColors: providerColorsConfigEnabled
            ? eventColorByStateObject.iconColors
            : undefined,
          appointmentIconColors: providerColorsConfigEnabled
            ? eventColorByStateObject.appointmentIconColors
            : undefined,
          isAppointmentTelehealth: appointment.isAppointmentTelehealth,
          telehealthAppointmentDetails:
            appointment.telehealthAppointmentDetails,
        },
      } as const;
    });
  }, [
    appointments,
    utcToZonedTimeIso,
    getProviderIndex,
    getProviderUserMedspa,
    providerColorsConfigEnabled,
    multiProviderAppointmentsV1Enabled,
    mapAppointmentServicesToCalendar,
  ]);
}

export function useCalendarAppointmentServices() {
  const { getProviderIndex, getProviderUserMedspa } = useProviderByUser();
  const { providerColorsConfigEnabled } = useFeatureFlags();

  const mapAppointmentServicesToCalendar = (
    appointmentServices: (Omit<
      AppointmentFieldsFragment["appointmentServices"][number],
      "id" | "__typename"
    > & {
      id?: string;
    })[]
  ) => {
    return appointmentServices.map((service) => {
      const providerUserMedspa = getProviderUserMedspa(
        service.provider.user.id
      );
      const providerIndex = getProviderIndex(service.provider.user.id);

      const { colorObjectByIndex, colorObjectConfigured } =
        getUserCalendarColor({
          ...service.provider,
          id: service.provider.user.id,
          firstName: service.provider.user.firstName,
          lastName: service.provider.user.lastName,
          color: providerUserMedspa?.color,
          userIndex: providerIndex,
        });

      const providerColorPalette = providerColorsConfigEnabled
        ? colorObjectConfigured
        : colorObjectByIndex;

      return {
        ...service,
        providerColorPalette,
      };
    });
  };

  return { mapAppointmentServicesToCalendar };
}
